import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';

const NullHoursSubmissions = ({ userData }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');

    fetch('/api/assignmentsubmissions/null-hours', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch assignment submissions with null hours');
        return res.json();
      })
      .then(data => {
        setSubmissions(data.submissions || []);
        setCount(data.submissions?.length || 0);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  // Function to determine if a submission should have hours awarded
  const shouldHaveHoursAwarded = (submission) => {
    const graderesult = submission.graderesult;
    
    if (graderesult === 'not-required') {
      return true; // Survey assignments should get hours
    } else if (graderesult && typeof graderesult === 'string' && graderesult.includes('%')) {
      return true; // Percentage-based submissions should get hours (0 or full)
    }
    
    return false; // needsEvaluation or unknown should remain null
  };

  // Separate submissions into categories
  const submissionsWithMissingHours = submissions.filter(shouldHaveHoursAwarded);
  const submissionsCorrectlyNull = submissions.filter(s => !shouldHaveHoursAwarded(s));

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <h1>Assignment Submissions with Null Hours</h1>
      
      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: 16, 
        marginBottom: 24 
      }}>
        <div style={{ 
          background: '#e3f2fd', 
          border: '2px solid #2196f3', 
          borderRadius: 8, 
          padding: 16,
          color: '#1565c0'
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            🔵 Missing Hours (Should Be Awarded)
          </div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {submissionsWithMissingHours.length}
          </div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            These submissions should have hours awarded based on their grading status
          </div>
        </div>

        <div style={{ 
          background: '#fff3e0', 
          border: '2px solid #ff9800', 
          borderRadius: 8, 
          padding: 16,
          color: '#e65100'
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            🟠 Correctly Null
          </div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {submissionsCorrectlyNull.length}
          </div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            These submissions correctly have null hours (need evaluation)
          </div>
        </div>

        <div style={{ 
          background: '#f3e5f5', 
          border: '2px solid #9c27b0', 
          borderRadius: 8, 
          padding: 16,
          color: '#4a148c'
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
            🟣 Total Null Hours
          </div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {count}
          </div>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            Total submissions with null hoursreceived
          </div>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 48, 
          color: '#666',
          background: '#f8f9fa',
          borderRadius: 8
        }}>
          <h3>No submissions found with null hours</h3>
          <p>All assignment submissions have hours assigned.</p>
        </div>
      ) : (
        <>
          {/* Submissions with Missing Hours (Should Be Awarded) */}
          {submissionsWithMissingHours.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ color: '#1565c0', marginBottom: 16 }}>
                🔵 Submissions Missing Hours (Should Be Awarded) - {submissionsWithMissingHours.length}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {submissionsWithMissingHours.map(s => {
                  const studentName = s.first_name && s.last_name 
                    ? `${s.first_name} ${s.last_name}` 
                    : s.primary_email || 'Unknown Student';
                  
                  const assignmentTitle = s.assignmenttitle || 'Untitled Assignment';
                  const projectTitle = s.projecttitle || 'No Project';
                  const projectCode = s.projectcode || 'N/A';
                  
                  let statusColor = '#333';
                  let statusText = s.graderesult || 'Unknown';
                  let expectedHours = 0;
                  let hoursReason = '';
                  
                  if (s.graderesult === 'not-required') {
                    statusColor = '#1976d2'; // blue
                    statusText = 'Survey - No Grading Required';
                    expectedHours = s.assignment_hours || 0;
                    hoursReason = 'Should get full assignment hours';
                  } else if (typeof s.graderesult === 'string' && s.graderesult.includes('%')) {
                    const percentage = parseFloat(s.graderesult.replace('%', ''));
                    statusColor = '#388e3c'; // green
                    statusText = `${s.graderesult} - Graded`;
                    if (percentage >= 70) {
                      expectedHours = s.assignment_hours || 0;
                      hoursReason = `Should get full hours (${percentage}% >= 70%)`;
                    } else {
                      expectedHours = 0;
                      hoursReason = `Should get 0 hours (${percentage}% < 70%)`;
                    }
                  }

                  return (
                    <div key={s.id} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      border: '3px solid #2196f3', 
                      borderRadius: 8, 
                      padding: 16, 
                      background: '#e3f2fd', 
                      boxShadow: '0 2px 8px rgba(33,150,243,0.2)',
                      position: 'relative'
                    }}>
                      {/* Missing Hours indicator */}
                      <div style={{
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        background: '#2196f3',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        🔵 MISSING HOURS
                      </div>

                      {/* Icon as View button */}
                      <div style={{ 
                        marginRight: 16, 
                        marginTop: 2, 
                        cursor: 'pointer', 
                        transition: 'transform 0.1s', 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}
                        title="View Submission"
                        onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${s.id}`, '_blank')}
                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <span role="img" aria-label="assignment" style={{ fontSize: 28, color: '#fbc02d' }}>📝</span>
                      </div>

                      {/* Main content */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, color: '#222' }}>
                          {assignmentTitle}
                        </div>
                        <div style={{ fontSize: 14, marginBottom: 4 }}>
                          <span style={{ color: statusColor, fontWeight: 500 }}>Status: {statusText}</span>
                          {s.assignment_hours && (
                            <span style={{ color: '#555', marginLeft: 8, fontSize: 13 }}>
                              (Assignment Hours: {s.assignment_hours})
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          <strong>Student:</strong> {studentName}
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          <strong>Project:</strong> {projectTitle} ({projectCode})
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          <strong>Submission ID:</strong> {s.id}
                        </div>
                        <div style={{ fontSize: 13, color: '#1565c0', fontWeight: 500, marginTop: 4 }}>
                          <strong>Expected Hours:</strong> {expectedHours} - {hoursReason}
                        </div>
                      </div>

                      {/* Dates and actions */}
                      <div style={{ textAlign: 'right', minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                          Created: {s.createddate ? new Date(s.createddate).toLocaleString() : 'N/A'}
                        </div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                          Updated: {s.updateddate ? new Date(s.updateddate).toLocaleString() : 'N/A'}
                        </div>
                        <button
                          onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${s.id}`, '_blank')}
                          style={{ 
                            padding: '8px 16px', 
                            borderRadius: 4, 
                            background: '#1976d2', 
                            color: 'white', 
                            border: 'none', 
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Grade Submission
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Submissions Correctly Null (Need Evaluation) */}
          {submissionsCorrectlyNull.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ color: '#e65100', marginBottom: 16 }}>
                🟠 Submissions Correctly Null (Need Evaluation) - {submissionsCorrectlyNull.length}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {submissionsCorrectlyNull.map(s => {
                  const studentName = s.first_name && s.last_name 
                    ? `${s.first_name} ${s.last_name}` 
                    : s.primary_email || 'Unknown Student';
                  
                  const assignmentTitle = s.assignmenttitle || 'Untitled Assignment';
                  const projectTitle = s.projecttitle || 'No Project';
                  const projectCode = s.projectcode || 'N/A';
                  
                  let statusColor = '#d32f2f';
                  let statusText = s.graderesult || 'Unknown';
                  
                  if (s.graderesult === 'needsEvaluation') {
                    statusColor = '#d32f2f'; // red
                    statusText = 'Needs Evaluation';
                  } else {
                    statusColor = '#666'; // gray for unknown
                    statusText = `Unknown: ${s.graderesult}`;
                  }

                  return (
                    <div key={s.id} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      border: '2px solid #ff9800', 
                      borderRadius: 8, 
                      padding: 16, 
                      background: '#fff3e0', 
                      boxShadow: '0 1px 4px rgba(255,152,0,0.2)',
                      position: 'relative'
                    }}>
                      {/* Correctly Null indicator */}
                      <div style={{
                        position: 'absolute',
                        top: -8,
                        left: 16,
                        background: '#ff9800',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        🟠 CORRECTLY NULL
                      </div>

                      {/* Icon as View button */}
                      <div style={{ 
                        marginRight: 16, 
                        marginTop: 2, 
                        cursor: 'pointer', 
                        transition: 'transform 0.1s', 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}
                        title="View Submission"
                        onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${s.id}`, '_blank')}
                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <span role="img" aria-label="assignment" style={{ fontSize: 28, color: '#fbc02d' }}>📝</span>
                      </div>

                      {/* Main content */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, color: '#222' }}>
                          {assignmentTitle}
                        </div>
                        <div style={{ fontSize: 14, marginBottom: 4 }}>
                          <span style={{ color: statusColor, fontWeight: 500 }}>Status: {statusText}</span>
                          {s.assignment_hours && (
                            <span style={{ color: '#555', marginLeft: 8, fontSize: 13 }}>
                              (Assignment Hours: {s.assignment_hours})
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          <strong>Student:</strong> {studentName}
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          <strong>Project:</strong> {projectTitle} ({projectCode})
                        </div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 2 }}>
                          <strong>Submission ID:</strong> {s.id}
                        </div>
                        <div style={{ fontSize: 13, color: '#e65100', fontWeight: 500, marginTop: 4 }}>
                          <strong>Hours Status:</strong> Correctly null - needs manual evaluation
                        </div>
                      </div>

                      {/* Dates and actions */}
                      <div style={{ textAlign: 'right', minWidth: 200 }}>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                          Created: {s.createddate ? new Date(s.createddate).toLocaleString() : 'N/A'}
                        </div>
                        <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                          Updated: {s.updateddate ? new Date(s.updateddate).toLocaleString() : 'N/A'}
                        </div>
                        <button
                          onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${s.id}`, '_blank')}
                          style={{ 
                            padding: '8px 16px', 
                            borderRadius: 4, 
                            background: '#ff9800', 
                            color: 'white', 
                            border: 'none', 
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Evaluate Submission
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NullHoursSubmissions; 